import React from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  IconButton,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { PieChart, Pie, Cell, ResponsiveContainer, Label } from "recharts";
import styles from "./contractType.module.scss";

interface ContractTypeProps {
  data?: {
    timeRange: string;
    totalContracts: number;
    breakdown: Array<{
      type: string;
      count: number;
    }>;
  };
}

const ContractType: React.FC<ContractTypeProps> = ({ data: apiData }) => {
  const colors = ["#08ADC2", "#00b895", "#80e6d0", "#b3f0e6"];

  const defaultData = [
    { label: "Employment Contracts", value: 48, percent: 48, color: "#08ADC2" },
    {
      label: "Non-Disclosure Agreement",
      value: 32,
      percent: 32,
      color: "#00b895",
    },
    { label: "Purchase Agreements", value: 8, percent: 8, color: "#80e6d0" },
    { label: "Lease Agreements", value: 12, percent: 12, color: "#b3f0e6" },
  ];

  const data = apiData
    ? apiData.breakdown.map((item, index) => ({
        label: item.type,
        value: item.count,
        percent: item.count,
        color: colors[index % colors.length],
      }))
    : defaultData;

  const totalContracts =
    apiData?.totalContracts ?? data.reduce((sum, item) => sum + item.value, 0);

  const renderCustomLabel = (props: any) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, value } = props;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#ffffff"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="16"
        fontWeight="600"
      >
        {String(value).padStart(2, "0")}
      </text>
    );
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Box className={styles.titleSection}>
          <Typography variant="h6" className={styles.title}>
            Contract type
          </Typography>
          <Typography variant="body2" className={styles.subtitle}>
            Visual summary of contracts by type.
          </Typography>
        </Box>
        <Box className={styles.headerActions}>
          <FormControl className={styles.filter}>
            <Select
              defaultValue="Last quarter"
              IconComponent={KeyboardArrowDownIcon}
              className={styles.select}
            >
              <MenuItem value="Last quarter">Last quarter</MenuItem>
            </Select>
          </FormControl>
          <IconButton className={styles.moreButton} aria-label="More options">
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Box>

      <Box className={styles.chartContainer}>
        <Box className={styles.legend}>
          {data.map((item, index) => (
            <Box key={index} className={styles.legendItem}>
              <Box
                className={styles.legendColor}
                style={{ backgroundColor: item.color }}
              />
              <Typography variant="body2" className={styles.legendLabel}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
        {/* <h1>hello</h1> */}

        <Box className={styles.semiCircleChart}>
          <Box className={styles.centerLabel}>
            <Typography className={styles.centerValue}>
              {totalContracts}
            </Typography>
            <Typography className={styles.centerText}>
              Total Contracts
            </Typography>
          </Box>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="100%"
                startAngle={180}
                endAngle={0}
                innerRadius={80}
                outerRadius={140}
                paddingAngle={2}
                dataKey="value"
                stroke="none"
                label={renderCustomLabel}
                labelLine={false}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Box>
  );
};

export default ContractType;
